import "./TeamDevelopmentActionPlanObjectivesSection.less";
import * as React from "react";
import { AtButton, AtCollapse, AtContainer, AtIcon, AtTitle, IconTitleSubtitle, Sizing } from "@atman/design-system";
import { Colors } from "../../../../styles";
import { CompDevActionPlanObjectiveRow } from "../../ActionPlanObjectiveRow";
import {
    CompetencyDevelopmentProcessResultsData,
    ICompetencyDevelopmentProcessAssessmentData,
    IObjectiveWithCompetency,
    PrintModeEnum,
} from "@atman/business";
import { DocumentSection } from "../../../DocumentSection";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { LearningResource } from "../../LearningResource";
import { ObjectiveApplication } from "../../ObjectiveApplication";
import { TeamDevelopmentActionPlanObjectiveCompScores } from "./TeamDevelopmentActionPlanObjectiveCompScores";
import { Trans, t } from "@lingui/macro";
import { observer } from "mobx-react";
import { sortArrayByStringProp } from "../../ResultTableContainer";

type ActionPlanObjectiveType = "leverage" | "improve";

export interface ITeamDevelopmentActionPlanObjectivesSectionProps {
    type: ActionPlanObjectiveType;
    candidateProcessResultsData: CompetencyDevelopmentProcessResultsData;
    processAssessmentData?: ICompetencyDevelopmentProcessAssessmentData;
    openAndMonitorTabFunc?: (url: string) => void;
    printMode?: PrintModeEnum;
}

export const TeamDevelopmentActionPlanObjectivesSection: React.FC<ITeamDevelopmentActionPlanObjectivesSectionProps> =
    observer((props) => {
        const { type, candidateProcessResultsData, processAssessmentData, openAndMonitorTabFunc, printMode } = props;

        const getIcon: () => IconProp = () => {
            switch (type) {
                case "leverage":
                    return ["far", "fist-raised"];
                case "improve":
                    return ["far", "bullseye-arrow"];
            }
        };

        const objectivesWithComps: IObjectiveWithCompetency[] = React.useMemo(() => {
            let items: IObjectiveWithCompetency[];

            switch (type) {
                case "leverage":
                    items = candidateProcessResultsData.getObjectivesToLeverage();
                    break;
                case "improve":
                    items = candidateProcessResultsData.getObjectivesToImprove();
                    break;
                default:
                    items = [];
                    break;
            }

            return sortArrayByStringProp(items, (x) => x.competency.name);
        }, [type, candidateProcessResultsData]);

        const isLeverage = type === "leverage";

        return (
            <AtContainer className="TeamDevelopmentActionPlanObjectivesSection">
                <div className="section-header">
                    <IconTitleSubtitle
                        icon={
                            <AtIcon
                                icon={getIcon()}
                                color={isLeverage ? Colors.greenBase : Colors.purpleBase}
                                bgColor={isLeverage ? Colors.greenShades.shade8 : Colors.purpleShades.shade8}
                                size="2x"
                            />
                        }
                        title={`competencyDevelopment.actionPlan.${type}Section.title`.localize()}
                        subtitle={`competencyDevelopment.actionPlan.${type}Section.description`.localize()}
                        size="lg"
                    />
                    {openAndMonitorTabFunc && processAssessmentData && (
                        <AtButton color="secondary" onClick={() => openAndMonitorTabFunc(processAssessmentData.url)}>
                            <Trans id="global.edit">Edit</Trans>
                        </AtButton>
                    )}
                </div>
                {objectivesWithComps.map((objectiveWithComp) => {
                    const selectedObjectiveLearningResources = objectiveWithComp.data.getSelectedLearningResources();
                    const selectedGlobalLearningResources = objectiveWithComp.competency.learningResources.filter((x) =>
                        objectiveWithComp.data.selectedLearningResourceIds.includes(x.id),
                    );

                    const selectedLearningResources = [
                        ...selectedGlobalLearningResources,
                        ...selectedObjectiveLearningResources,
                    ];
                    const selectedApplications = objectiveWithComp.data.getSelectedApplications();

                    return (
                        <CompDevActionPlanObjectiveRow
                            objective={objectiveWithComp}
                            key={objectiveWithComp.data.id}
                            printMode={printMode}
                        >
                            <DocumentSection className="scores-section">
                                <TeamDevelopmentActionPlanObjectiveCompScores objective={objectiveWithComp} />
                            </DocumentSection>

                            {(selectedApplications.length > 0 ||
                                (objectiveWithComp.data.applicationNotes?.length ?? 0) > 0) && (
                                <AtCollapse
                                    toggleRowContent={`${
                                        selectedApplications.length
                                    } ${`competencyDevelopment.actionPlan.${type}Section.objectiveApplications`.localize()}`}
                                    className="applications-subsection"
                                    toggleRowSize={"sm"}
                                    chevronSize="sm"
                                    defaultOpen
                                >
                                    <DocumentSection
                                        className="subsection-details"
                                        style={{ marginBottom: `-${Sizing.getPixelsPropValue("xs")}` }}
                                    >
                                        <div className="action-plan-subsection-title">
                                            {`competencyDevelopment.actionPlan.${type}Section.objectiveApplications.title`.localize()}
                                        </div>
                                        <div className="action-plan-subsection-description">
                                            {`competencyDevelopment.actionPlan.${type}Section.objectiveApplications.description.readOnly`.localize()}
                                        </div>
                                    </DocumentSection>
                                    <DocumentSection className="subsection-content">
                                        {selectedApplications.map((application, i) => (
                                            <ObjectiveApplication
                                                key={`application-${i}`}
                                                application={application}
                                                printMode={printMode}
                                            />
                                        ))}
                                    </DocumentSection>
                                    {objectiveWithComp.data.applicationNotes ? (
                                        <>
                                            <DocumentSection className="notes-subsection-details">
                                                <AtTitle
                                                    headingType={5}
                                                    title={t({
                                                        id: "competencyDevelopment.actionPlan.additionalNotes.title",
                                                        message: "Additional notes",
                                                    })}
                                                    className="action-plan-notes-subsection-title"
                                                />
                                                <div className="notes-container">
                                                    {objectiveWithComp.data.applicationNotes.split("\n").map((x, i) => (
                                                        <div key={`observable-behavior-note-${i}`}>{x}</div>
                                                    ))}
                                                </div>
                                            </DocumentSection>
                                        </>
                                    ) : null}
                                </AtCollapse>
                            )}

                            {(selectedLearningResources.length > 0 ||
                                (objectiveWithComp.data.learningResourceNotes?.length ?? 0) > 0) && (
                                <AtCollapse
                                    toggleRowContent={`${
                                        selectedLearningResources.length
                                    } ${"competencyDevelopment.learningResources".localize()}`}
                                    className="learning-resources-subsection"
                                    toggleRowSize={"sm"}
                                    chevronSize="sm"
                                    defaultOpen
                                >
                                    <DocumentSection
                                        className="subsection-details"
                                        style={{ marginBottom: `-${Sizing.getPixelsPropValue("xs")}` }}
                                    >
                                        <div className="action-plan-subsection-title">
                                            {"competencyDevelopment.learningResources.title".localize()}
                                        </div>
                                        <div className="action-plan-subsection-description">
                                            {"competencyDevelopment.learningResources.description.readOnly".localize()}
                                        </div>
                                    </DocumentSection>
                                    <DocumentSection
                                        className="subsection-content"
                                        style={{ marginBottom: `-${Sizing.getPixelsPropValue("sm")}` }}
                                    >
                                        <div className="learning-resources">
                                            {selectedLearningResources.map((learningResource, i) => (
                                                <div key={`learning-resource-${i}`} className="learning-resource-cell">
                                                    <LearningResource learningResource={learningResource} />
                                                </div>
                                            ))}
                                        </div>
                                    </DocumentSection>
                                    {objectiveWithComp.data.learningResourceNotes ? (
                                        <>
                                            <DocumentSection className="notes-subsection-details">
                                                <AtTitle
                                                    headingType={5}
                                                    title={t({
                                                        id: "competencyDevelopment.actionPlan.additionalNotes.title",
                                                        message: "Additional notes",
                                                    })}
                                                    className="action-plan-notes-subsection-title"
                                                />
                                                <div className="notes-container">
                                                    {objectiveWithComp.data.learningResourceNotes
                                                        .split("\n")
                                                        .map((x, i) => (
                                                            <div key={`observable-behavior-note-${i}`}>{x}</div>
                                                        ))}
                                                </div>
                                            </DocumentSection>
                                        </>
                                    ) : null}
                                </AtCollapse>
                            )}
                        </CompDevActionPlanObjectiveRow>
                    );
                })}
            </AtContainer>
        );
    });
